/**
 * Feld, das beim Ueberfahren eines Menueeintrags aufgeht.
 *
 * Der einzige Grund fuer dieses Skript ist der Weichzeichner. Der Header traegt
 * auf allen Seiten ausser der Startseite selbst ein backdrop-filter und wird
 * dadurch zur eigenen Zeichenflaeche. Ein Feld, das darin steckt, findet nichts
 * mehr vor, was es weichzeichnen koennte - es bliebe einfach durchsichtig.
 *
 * Also zieht das Feld beim Start aus dem Header aus und haengt sich direkt an
 * den Body. Von dort sieht es die Seite darunter und das Milchglas greift. Weil
 * es damit seinen Platz im Fluss verliert, richtet es sich bei jedem Oeffnen
 * neu an seinem Bezugselement aus.
 *
 * data-lage bestimmt, wo: "unten" mittig darunter, "rechts" daneben. Genutzt
 * von beiden Feldern im Menue - dem Hinweis am Menuepunkt und der Vorschau
 * eines Produkts.
 */
class DpMenuHint extends HTMLElement {
  connectedCallback() {
    if (this.aufgebaut) return;
    this.aufgebaut = true;

    // Der Menuepunkt bleibt der Bezug, auch wenn das Feld gleich umzieht.
    this.anker = this.parentElement;
    if (!this.anker || this.anker === document.body) return;

    this.zeige = this.zeige.bind(this);
    this.verbirg = this.verbirg.bind(this);
    this.richteAus = this.richteAus.bind(this);

    /*
     * Erst umziehen, dann horchen. Der Umzug meldet das Element kurz ab, und
     * disconnectedCallback raeumt dabei alle Zuhoerer wieder ab - haenge man
     * sie vorher an, waeren sie danach weg.
     */
    document.body.appendChild(this);

    this.anker.addEventListener('pointerenter', this.zeige);
    this.anker.addEventListener('pointerleave', this.verbirg);
    this.anker.addEventListener('focusin', this.zeige);
    this.anker.addEventListener('focusout', this.verbirg);
  }

  disconnectedCallback() {
    if (!this.anker) return;
    this.anker.removeEventListener('pointerenter', this.zeige);
    this.anker.removeEventListener('pointerleave', this.verbirg);
    this.anker.removeEventListener('focusin', this.zeige);
    this.anker.removeEventListener('focusout', this.verbirg);
    window.removeEventListener('resize', this.richteAus);
  }

  /**
   * Setzt das Feld mittig unter seinen Menuepunkt.
   *
   * Gemessen wird gegen das Fenster, nicht gegen die Seite: der Header ist
   * fest, das Feld ist es damit auch, und beim Scrollen bleibt beides beisammen.
   */
  richteAus() {
    const kasten = this.anker.getBoundingClientRect();
    const rand = 12;
    const rechts = this.dataset.lage === 'rechts';

    if (rechts) {
      // Daneben: oben buendig, und wenn rechts kein Platz mehr ist, davor.
      this.style.top = Math.max(rand, kasten.top) + 'px';

      let links = kasten.right - 14;
      if (links + this.offsetWidth + rand > window.innerWidth) {
        links = kasten.left - this.offsetWidth + 14;
      }
      this.style.left = Math.max(rand, links) + 'px';

      // Unten darf es nicht aus dem Fenster laufen.
      const unten = parseFloat(this.style.top) + this.offsetHeight;
      if (unten + rand > window.innerHeight) {
        this.style.top = Math.max(rand, window.innerHeight - this.offsetHeight - rand) + 'px';
      }
      return;
    }

    this.style.top = kasten.bottom + 10 + 'px';

    // Am Fensterrand darf das Feld nicht heraushaengen.
    const halb = this.offsetWidth / 2;
    const mitte = kasten.left + kasten.width / 2;
    this.style.left = Math.min(
      Math.max(mitte, halb + rand),
      window.innerWidth - halb - rand
    ) + 'px';
  }

  zeige() {
    this.classList.add('dp-overlay-offen');
    this.richteAus();
    window.addEventListener('resize', this.richteAus, { passive: true });
  }

  verbirg() {
    this.classList.remove('dp-overlay-offen');
    window.removeEventListener('resize', this.richteAus);
  }
}

if (!customElements.get('dp-menu-hint')) {
  customElements.define('dp-menu-hint', DpMenuHint);
}
